import type { ReceiptRow } from 'src/features/payment/utils/receiptRow';

/**
 * Turning fiscal document rows into something a canvas can draw.
 *
 * The rules mirror `DocumentRenderer` in the cloud-fiscalisation library, which
 * draws the PNG attached to confirmation emails, and the identical file in the
 * clients portal. All three read the same rows from the same endpoint, and the
 * receipt a manager looks at must not differ from the one in the customer's
 * inbox - so a change to one has to reach the others.
 *
 * The layout lives in the attributes, not in the text:
 *
 *  - a CENTERED row arrives SHORT and unpadded, because the printer centres it.
 *    Drawing it verbatim puts the whole company header flush left.
 *  - a DOUBLE_WIDTH row arrives with HALF the characters, because each glyph
 *    prints twice as wide. That is arithmetic from the flag, not a quirk.
 */
class ReceiptLayout {
    /**
     * Line heights a QR row claims, and the error-correction level it is encoded
     * at. Both mirror cloud-fiscalisation's renderer so the code on screen and
     * the one in the emailed PNG are the same square.
     */
    static readonly QR_LINES = 8;
    static readonly QR_ERROR_CORRECTION = 'M';

    static readonly BOLD = 'bold';
    static readonly CENTERED = 'centered';
    static readonly DOUBLE_WIDTH = 'double_width';
    static readonly DOUBLE_HEIGHT = 'double_height';
    static readonly TRIPLE_HEIGHT = 'triple_height';

    static has(row: ReceiptRow, attribute: string): boolean {
        return row.attributes.includes(attribute);
    }

    /** Characters this row may hold - halved when each glyph prints double. */
    static columnsFor(row: ReceiptRow, columns: number): number {
        const width = Math.max(columns, 1);
        return this.has(row, this.DOUBLE_WIDTH) ? Math.max(Math.floor(width / 2), 1) : width;
    }

    /** The row as a line padded to its full width, so every line aligns. */
    static lineFor(row: ReceiptRow, columns: number): string {
        const width = this.columnsFor(row, columns);

        if (row.kind === 'half') return ' '.repeat(width);
        if (row.kind === 'separator') return '-'.repeat(width);
        if (row.kind === 'barcode') return this.centre(row.barcode_data ?? '', width);

        return this.has(row, this.CENTERED) ? this.centre(row.text.trim(), width) : row.text.padEnd(width);
    }

    /**
     * How many line heights this row occupies.
     *
     * A QR is square, so it needs far more than one - but only when there is a
     * payload to draw, otherwise reserving eight leaves a hole in the receipt.
     */
    static heightScale(row: ReceiptRow): number {
        if (row.kind === 'barcode') return this.qrPayload(row) ? this.QR_LINES : 1;
        if (this.has(row, this.TRIPLE_HEIGHT)) return 3;
        if (this.has(row, this.DOUBLE_HEIGHT)) return 2;
        return 1;
    }

    /** The barcode data worth drawing as a QR, if there is any. */
    static qrPayload(row: ReceiptRow): string | null {
        if (row.kind !== 'barcode') return null;
        const payload = (row.barcode_data ?? '').trim();
        return payload || null;
    }

    static widthScale(row: ReceiptRow): number {
        return this.has(row, this.DOUBLE_WIDTH) ? 2 : 1;
    }

    /**
     * The document's column count.
     *
     * The endpoint reports it, but a receipt stored before that field existed
     * has none, so fall back to the widest row - counting a double-width row as
     * twice its characters, which is the space it actually takes.
     */
    static columnsOf(rows: ReceiptRow[], reported?: number): number {
        if (reported && reported > 0) return reported;

        const widest = rows.reduce((widest, row) => Math.max(widest, row.text.length * this.widthScale(row)), 0);
        return Math.max(widest, 1);
    }

    /** String.padStart/padEnd around the middle - the canvas draws left-aligned. */
    private static centre(text: string, width: number): string {
        if (text.length >= width) return text;
        const left = Math.floor((width - text.length) / 2);
        return text.padStart(left + text.length).padEnd(width);
    }
}

export { ReceiptLayout };
